import * as React from "react";

import { cn } from "@/lib/utils";

// A small, dependency-free markdown renderer for AI summaries: paragraphs, unordered/ordered
// lists, ATX headings, and inline bold / italic / code / links. Not full CommonMark — just
// what the summarizer emits. Unknown syntax degrades to plain text.

const INLINE =
  /(`[^`]+`|\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_|\[[^\]]+\]\([^)\s]+\))/g;

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-${i++}`;
    if (tok.startsWith("`")) {
      out.push(
        <code
          key={key}
          className="rounded bg-elevated px-1 py-0.5 font-mono text-[0.85em]"
        >
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith("**") || tok.startsWith("__")) {
      out.push(
        <strong key={key} className="font-semibold text-text">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else if (tok.startsWith("[")) {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
      out.push(
        link ? (
          <a
            key={key}
            href={link[2]}
            target="_blank"
            rel="noreferrer"
            className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
          >
            {link[1]}
          </a>
        ) : (
          tok
        ),
      );
    } else {
      out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const isUl = (l: string) => /^\s*[-*+]\s+/.test(l);
const isOl = (l: string) => /^\s*\d+\.\s+/.test(l);
const isHeading = (l: string) => /^#{1,6}\s+/.test(l);

export function Markdown({
  source,
  className,
}: {
  source: string;
  className?: string;
}) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let b = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = Math.min(heading[1].length + 2, 6);
      const Tag = `h${level}` as keyof React.JSX.IntrinsicElements;
      blocks.push(
        <Tag key={b} className="mt-4 font-semibold text-text first:mt-0">
          {renderInline(heading[2], `h${b}`)}
        </Tag>,
      );
      b++;
      i++;
      continue;
    }

    if (isUl(line) || isOl(line)) {
      const ordered = isOl(line);
      const items: React.ReactNode[] = [];
      while (i < lines.length && (ordered ? isOl(lines[i]) : isUl(lines[i]))) {
        const content = lines[i].replace(
          ordered ? /^\s*\d+\.\s+/ : /^\s*[-*+]\s+/,
          "",
        );
        items.push(
          <li key={items.length}>
            {renderInline(content, `li${b}-${items.length}`)}
          </li>,
        );
        i++;
      }
      const ListTag = ordered ? "ol" : "ul";
      blocks.push(
        <ListTag
          key={b}
          className={cn(
            "my-2 space-y-1 pl-5",
            ordered ? "list-decimal" : "list-disc",
          )}
        >
          {items}
        </ListTag>,
      );
      b++;
      continue;
    }

    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !isHeading(lines[i]) &&
      !isUl(lines[i]) &&
      !isOl(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={b} className="my-2 leading-relaxed first:mt-0">
        {renderInline(para.join(" "), `p${b}`)}
      </p>,
    );
    b++;
  }

  return (
    <div className={cn("text-sm text-text [&>*:last-child]:mb-0", className)}>
      {blocks}
    </div>
  );
}
